import { Box, Title } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import { JournalLineReconciliationFragment, TaxTypeFragment } from 'generated/resources/graphql'

import DayPicker from 'modules/core/components/DayPicker'
import MoneyField from 'modules/core/components/MoneyField'
import TextField from 'modules/core/components/TextField'
import { dateBackendToUI, dateUIToBackend } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import CurrencySelector from 'modules/finance/components/CurrencySelector'
import { FinancialDocumentSelector } from 'modules/finance/components/FinancialDocumentSelector'
import { DisabledFields } from 'modules/finance/components/Invoices/Detail/Content/Form/_utils'
import { InvoiceForm } from 'modules/finance/components/Invoices/Detail/Content/Form/types'
import type {
  InvoiceType,
  LegalEntity,
} from 'modules/finance/components/Invoices/Detail/Content/Form/types'
import FileDownloadCard from 'modules/finance/components/Invoices/FileDownloadCard'
import { canUpdateInvoiceData } from 'modules/finance/lib/invoices'

import { BasicInformation } from './BasicInformation'
import { DocumentTypeSelector } from './_DocumentTypeSelector'
import { LedgerAccountSection } from './_LedgerAccountSection'
import { LegalEntitySection } from './_LegalEntitySection'
import { ReconciliationSection } from './_ReconciliationSection'
import { TaxesSection } from './_TaxesSection'
import { VendorSection } from './_VendorSection'

interface Props {
  invoice: InvoiceType
  form: FormikProps<InvoiceForm>
  defaultValues: InvoiceForm
  disabledFields: DisabledFields
  legalEntities: LegalEntity[]
  showReconciliationSection: boolean
  canManageJournalEntries: boolean
  taxTypes?: TaxTypeFragment[]
  canReconcileWithJournalLines: boolean
  canReconcileWithFinancialDocuments: boolean
  journalLines: JournalLineReconciliationFragment[]
}

export const BasicInfoTab = ({
  invoice,
  form,
  disabledFields,
  legalEntities,
  showReconciliationSection,
  canManageJournalEntries,
  taxTypes,
  canReconcileWithJournalLines,
  canReconcileWithFinancialDocuments,
  journalLines,
}: Props) => {
  const invoiceEditable = canUpdateInvoiceData(invoice)

  const extractDateValue = (date: string | undefined) =>
    date !== undefined && date !== '' ? dateBackendToUI(date) : ''

  const showFinancialDocumentSelector = form.values.documentType === 'credit_note'

  return (
    <>
      <BasicInformation
        status={invoice.status}
        createdAt={invoice.createdAt}
        validatedAt={invoice.validatedAt}
      />
      <Box gap="s16">
        <Box flexDirection="row" gap="s24">
          <Box style={{ flex: '1' }}>
            <DocumentTypeSelector
              selectedDocumentType={form.values.documentType}
              readOnly={disabledFields?.documentType ?? false}
              onSelectDocumentTypeSelector={(value) => {
                form.setValues({ ...form.values, documentType: value })
              }}
            />
          </Box>

          <Box style={{ flex: '1' }}>
            {showFinancialDocumentSelector && (
              <FinancialDocumentSelector
                label={i18n.t('finance.invoices.detail.fields.original_invoice')}
                documentTypes={['invoice']}
                companyId={invoice.companyId}
                value={form.values.parentFinancialDocument}
                onSelect={(value) => {
                  form.setValues({ ...form.values, parentFinancialDocument: value })
                }}
                disabled={disabledFields?.parentFinancialDocument ?? false}
                toDate={form.values.documentDate}
                {...(form.values.vendor?.id !== '0' && { vendorId: form.values.vendor?.id })}
              />
            )}
          </Box>
        </Box>

        <Box flexDirection="row" gap="s24">
          <DayPicker
            readonly={disabledFields?.documentDate}
            label={i18n.t('finance.invoices.detail.fields.document_date')}
            placeholder={i18n.t('finance.invoices.detail.placeholders.document_date')}
            value={extractDateValue(form.values.documentDate)}
            onChange={(value) =>
              form.setValues({ ...form.values, documentDate: dateUIToBackend(value) })
            }
            error={form.errors.documentDate}
          />
          <DayPicker
            readonly={disabledFields?.dueDate}
            label={i18n.t('finance.invoices.detail.fields.due_date')}
            value={extractDateValue(form.values.dueDate)}
            onChange={(value) =>
              form.setValues({ ...form.values, dueDate: dateUIToBackend(value) })
            }
            placeholder={i18n.t('finance.invoices.detail.placeholders.due_date')}
            error={form.errors.dueDate}
          />
        </Box>

        <TextField
          readonly={disabledFields?.documentNumber}
          label={i18n.t('finance.invoices.detail.fields.invoice_number')}
          value={form.values.documentNumber || ''}
          onChange={(value) => form.setValues({ ...form.values, documentNumber: value })}
          error={form.errors.documentNumber}
        />

        <Box flexDirection="row" gap="s24" alignItems="flexStart">
          <MoneyField
            readonly={disabledFields?.totalAmountCents}
            maxDecimals={2}
            step={1}
            label={i18n.t('finance.invoices.detail.fields.document_amount')}
            currency={form.values.currency ?? ''}
            value={form.values.totalAmountCents.toString()}
            onChange={(value) => form.setValues({ ...form.values, totalAmountCents: value })}
            error={form.errors.totalAmountCents}
          />

          <CurrencySelector
            value={form.values.currency ?? ''}
            error={form.errors.currency}
            onChange={(value) => form.setValues({ ...form.values, currency: value })}
            isDisabled={disabledFields?.currency}
          />
        </Box>

        <TaxesSection
          countryCode={form.values.vendor?.address?.countryCode}
          form={form}
          readonly={disabledFields?.taxes ?? false}
          taxTypes={taxTypes}
        />

        <LegalEntitySection
          form={form}
          legalEntities={legalEntities}
          readonly={disabledFields?.taxes ?? false}
        />

        <LedgerAccountSection
          form={form}
          accountResource={invoice.accountResource}
          legalEntities={legalEntities}
          readonly={disabledFields?.ledgerAccount ?? false}
          canManageJournalEntries={canManageJournalEntries}
        />

        <VendorSection form={form} invoice={invoice} readOnly={!invoiceEditable} />

        <ReconciliationSection
          invoice={invoice}
          showReconciliationSection={showReconciliationSection}
          canReconcileWithJournalLines={canReconcileWithJournalLines}
          canReconcileWithFinancialDocuments={canReconcileWithFinancialDocuments}
          journalLines={journalLines}
        />

        {invoice.file && (
          <Box gap="s8" paddingTop="s8">
            <Title variant="400" title={i18n.t('finance.invoices.detail.files.title')} />
            <FileDownloadCard {...invoice.file} />
          </Box>
        )}
      </Box>
    </>
  )
}
